'use client';

/**
 * EmployeeSelfServiceModal — full-screen ESS ("My HR Portal") overlay.
 *
 * Mounts the real MyHrPortalWorkspace (the same shell UnifiedDashboard uses
 * for its `myhr` view) inside a viewport-covering portal so employees can get
 * to payslips, leave, timesheets, overtime, documents, loans, and reviews
 * without navigating away — the same pattern as IamConsoleModal /
 * SupportModal / ProfileModal.
 *
 * The workspace is loaded via next/dynamic so the Header bundle doesn't pay
 * for it until first opened. WorkforcePortalDashboard renders its own header
 * with a close button, so this modal is intentionally just the overlay.
 *
 * Open it by dispatching `window.dispatchEvent(new CustomEvent('ess-portal-open',
 * { detail: { tab?: WorkforcePortalTab, section?: WorkforcePortalSection } }))`
 * — Header listens for the event and mounts the modal.
 */

import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import dynamic from 'next/dynamic';
import { Users, X } from 'lucide-react';
import { useNavigation } from '@/hooks/useNavigation';
import { useTheme } from '@/contexts/ThemeContext';
import { adjustColorBrightness } from '@/lib/themes';
import type { NavigationItem } from '@/lib/navigationService';
import type { WorkforcePortalTab } from '@/app/client-zone/dashboard/components/dashboards/customer-portals/WorkforceSelfServicePortal';
import type { WorkforcePortalSection } from '@/app/client-zone/dashboard/components/dashboards/WorkforcePortalDashboard';

const MyHrPortalWorkspaceStandalone = dynamic(
  () =>
    import(
      '@/app/client-zone/dashboard/components/dashboards/MyHrPortalWorkspace'
    ).then((mod) => mod.MyHrPortalWorkspaceStandalone),
  { ssr: false },
);

interface EmployeeSelfServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Which workspace tab to open on (payslips, leave, timesheets, ...). */
  initialTab?: WorkforcePortalTab;
  /** Which section to open on — 'workspace' (default) or 'support'. */
  initialSection?: WorkforcePortalSection;
}

export default function EmployeeSelfServiceModal({
  isOpen,
  onClose,
  initialTab,
  initialSection,
}: EmployeeSelfServiceModalProps) {
  const { navigationGroups, navigationData } = useNavigation();
  const { clientBranding } = useTheme();
  // Same chrome rule as IamConsoleModal: brand primary (with a subtle deepen)
  // for the header icon — the workspace's own band uses secondary_color.
  const brandPrimary = clientBranding?.primary_color || '#9333ea';
  const brandGradient = `linear-gradient(135deg, ${brandPrimary}, ${adjustColorBrightness(brandPrimary, -10)})`;

  // Same gate as the dashboard ESS tile: total-workforce or total-attendance
  // module access, or historical (read-only ex-employee) access.
  const hasEssAccess = useMemo(() => {
    const hasModule = Object.values(navigationGroups || {}).some((group) =>
      group.items?.some(
        (item: NavigationItem) =>
          item.module_key === 'total-workforce' || item.module_key === 'total-attendance',
      ),
    );
    return hasModule || !!navigationData?.historical_access;
  }, [navigationGroups, navigationData?.historical_access]);

  if (!isOpen || !hasEssAccess || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex h-[100dvh] flex-col overflow-hidden bg-gray-50 dark:bg-gray-900"
      data-tour-id="ess-portal-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Employee Self-Service"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-24 bg-gradient-to-b from-black/15 to-transparent dark:from-black/30" />

      {/* Header — matches IamConsoleModal / ProfileModal: centred icon + title,
          absolute X. The workspace renders its own tab bar + stat band below. */}
      <div className="relative flex-shrink-0 px-4 pb-1 pt-3 sm:pt-4">
        <div className="text-center">
          <div
            className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full text-white shadow-lg"
            style={{ backgroundImage: brandGradient }}
          >
            <Users className="h-6 w-6" />
          </div>
          <h1 className="pm-heading-1 text-lg sm:text-2xl text-gray-900 dark:text-white">Employee Self-Service</h1>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-3 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-gray-400 transition-colors hover:border-gray-300 hover:text-gray-600 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-500 dark:hover:border-gray-600 dark:hover:text-gray-300"
          aria-label="Close Employee Self-Service"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <MyHrPortalWorkspaceStandalone
          onClose={onClose}
          initialTab={initialTab}
          initialSection={initialSection}
        />
      </div>
    </div>,
    document.body,
  );
}
